/* TORRAS Life - 视频角度切换
 * 点击底部按钮把舞台视频平滑过渡到各自的目标进度。
 * 正向用 video.play() 原生播放（最丝滑），反向用 GSAP 补间 seek。
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 749px)';
  var TRANSITION = 0.9; // 期望过渡时长（秒）
  var MIN_RATE = 0.6;
  var MAX_RATE = 3;
  // 预烘焙帧采样数与抓取分辨率（移动端更省内存）
  var BAKE_SAMPLES_DESKTOP = 40;
  var BAKE_SAMPLES_MOBILE = 28;
  var BAKE_MAXW_DESKTOP = 1000;
  var BAKE_MAXW_MOBILE = 640;

  // 全局单例：按需加载主题内置的 GSAP（gsap.min.js）
  var gsapPromise = null;
  function ensureGsap(src) {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (gsapPromise) return gsapPromise;
    gsapPromise = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-mas-gsap]');
      var onload = function () {
        if (window.gsap) resolve(window.gsap);
        else reject(new Error('GSAP missing after load'));
      };
      if (existing) {
        existing.addEventListener('load', onload);
        existing.addEventListener('error', reject);
        if (window.gsap) resolve(window.gsap);
        return;
      }
      if (!src) { reject(new Error('No GSAP src')); return; }
      var s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.setAttribute('data-mas-gsap', '');
      s.onload = onload;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return gsapPromise;
  }

  function clamp01(value) {
    return Math.max(0, Math.min(1, value));
  }

  function hydrateVideoSources(video) {
    if (!video || video.dataset.masSourcesReady === 'true') return;
    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;
    sources.forEach(function (source) {
      if (!source.getAttribute('src')) {
        source.setAttribute('src', source.getAttribute('data-src'));
      }
    });
    video.dataset.masSourcesReady = 'true';
  }

  function StagePlayer(stage) {
    this.stage = stage;
    this.desktopVideo = stage.querySelector('.model-angle-switcher__video--desktop');
    this.mobileVideo = stage.querySelector('.model-angle-switcher__video--mobile');
    this.desktopReverseVideo = stage.querySelector('.model-angle-switcher__video--desktop-reverse');
    this.mobileReverseVideo = stage.querySelector('.model-angle-switcher__video--mobile-reverse');
    this.hasReverse = stage.dataset.hasReverse === 'true';
    this.reverseVideo = null;
    this.visibleVideo = null;
    this.renderMode = stage.dataset.renderMode || 'poster';
    this.gsapSrc = stage.dataset.gsapSrc || '';
    this.introSeconds = parseFloat(stage.dataset.introSeconds);
    if (!isFinite(this.introSeconds) || this.introSeconds < 0) this.introSeconds = 0.5;
    this.transition = parseFloat(stage.dataset.transition);
    if (!isFinite(this.transition) || this.transition <= 0) this.transition = TRANSITION;
    this.displayProgress = 0;
    this.targetProgress = 0;
    this.pendingTarget = null;
    this.pendingIntroSeconds = null;
    this.motionRaf = null;
    this.tween = null;
    this.frameTween = null;
    this.seekThreshold = 1 / 120;
    this.isSeeking = false;
    this.pendingSeekTime = null;
    this.seekWatchdog = null;
    this.activeVideo = null;
    this.canvas = stage.querySelector('.model-angle-switcher__canvas');
    this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
    this.frames = [];
    this.frameCount = 0;
    this.baked = false;
    this.baking = false;
    this.bakeVideo = null;
    this.bakeCanvas = null;
    this.bakeCtx = null;
    this.bakeIndex = 0;
    this.bakeObjectUrl = null;
    this.resizeRaf = null;
    this.canBake = (typeof window.createImageBitmap === 'function') && !!this.ctx;
    this.videoQuery = window.matchMedia(MOBILE_QUERY);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mediaChangeHandler = this.handleMediaChange.bind(this);

    if (this.videoQuery.addEventListener) {
      this.videoQuery.addEventListener('change', this.mediaChangeHandler);
    } else if (this.videoQuery.addListener) {
      this.videoQuery.addListener(this.mediaChangeHandler);
    }

    this.attachReadyListeners();
    this.selectActiveVideo();
    if (this.renderMode === 'video' && this.activeVideo) {
      this.prepareVideo(this.activeVideo, true);
      if (this.reverseVideo) this.prepareVideo(this.reverseVideo, false);
      // 预热 GSAP，按钮点击/补间可立即可用
      ensureGsap(this.gsapSrc).catch(function () {});
    }

    var self = this;
    this.onResize = function () {
      if (self.resizeRaf) return;
      self.resizeRaf = window.requestAnimationFrame(function () {
        self.resizeRaf = null;
        self.resizeCanvas();
      });
    };
    window.addEventListener('resize', this.onResize);
  }

  StagePlayer.prototype.selectActiveVideo = function () {
    var isMobile = this.videoQuery.matches;
    this.activeVideo = isMobile
      ? (this.mobileVideo || this.desktopVideo)
      : (this.desktopVideo || this.mobileVideo);
    this.reverseVideo = isMobile
      ? (this.mobileReverseVideo || this.desktopReverseVideo)
      : (this.desktopReverseVideo || this.mobileReverseVideo);
    var forward = this.activeVideo;
    var reverse = this.reverseVideo;
    [this.desktopVideo, this.mobileVideo, this.desktopReverseVideo, this.mobileReverseVideo].forEach(function (video) {
      if (video) video.classList.toggle('is-active', video === forward || video === reverse);
    });
    // 默认显示正向视频；切换显示由 setVisibleVideo 统一管理
    this.setVisibleVideo(forward);
  };

  StagePlayer.prototype.setRenderMode = function (mode) {
    this.renderMode = mode;
    if (this.stage) this.stage.dataset.renderMode = mode;
  };

  // 同一时刻只显示一个视频（正向或倒放），避免叠加闪烁
  StagePlayer.prototype.setVisibleVideo = function (video) {
    this.visibleVideo = video || null;
    var shown = this.visibleVideo;
    [this.desktopVideo, this.mobileVideo, this.desktopReverseVideo, this.mobileReverseVideo].forEach(function (v) {
      if (v) v.classList.toggle('is-shown', v === shown);
    });
  };

  // 切换到目标视频显示前，先把它 seek 到对齐帧，避免闪到旧画面
  StagePlayer.prototype.ensureVisible = function (video, atTime, onReady) {
    if (!video) { onReady(); return; }
    if (this.visibleVideo === video) { onReady(); return; }
    var self = this;
    var safe = Math.max((video.duration || 0) - 0.04, 0);
    var t = Math.max(0, Math.min(atTime, safe));
    if (!isFinite(video.duration) || Math.abs(video.currentTime - t) < this.seekThreshold) {
      this.setVisibleVideo(video);
      onReady();
      return;
    }
    var done = false;
    var wd = null;
    var finish = function () {
      if (done) return;
      done = true;
      if (wd) window.clearTimeout(wd);
      video.removeEventListener('seeked', finish);
      self.setVisibleVideo(video);
      onReady();
    };
    video.addEventListener('seeked', finish);
    wd = window.setTimeout(finish, 220);
    try { video.currentTime = t; } catch (e) { finish(); }
  };

  StagePlayer.prototype.attachReadyListeners = function () {
    var self = this;
    [this.desktopVideo, this.mobileVideo].forEach(function (video) {
      if (!video) return;
      ['loadeddata', 'canplay', 'seeked'].forEach(function (eventName) {
        video.addEventListener(eventName, function () {
          self.markReady(video);
        });
      });
      // 串行 seek 推进：上一次 seek 解码完成后才发起下一次，避免积压导致瞬移
      video.addEventListener('seeked', function () {
        self.onSeeked(video);
      });
    });
  };

  StagePlayer.prototype.markReady = function (video) {
    if (video === this.activeVideo && video.readyState >= 2) {
      this.stage.dataset.videoFrameReady = 'true';
    }
  };

  // 预热视频：静音 play→pause，确保有可 seek 的首帧；就绪后同步到当前进度
  // isPrimary=true 表示正向主视频，预热完成后驱动 flushPending/烘焙
  StagePlayer.prototype.prepareVideo = function (video, isPrimary) {
    if (!video) return;
    var self = this;
    hydrateVideoSources(video);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    var ready = function () {
      self.markReady(video);
      if (isPrimary) self.onVideoReady();
    };
    var warm = function () {
      var played = video.play();
      if (played && typeof played.then === 'function') {
        played.then(function () {
          try { video.pause(); } catch (error) {}
          ready();
        }).catch(function () {
          if (isPrimary) self.onVideoReady();
        });
      } else {
        try { video.pause(); } catch (error) {}
        ready();
      }
    };
    if (video.readyState >= 2) {
      warm();
    } else {
      video.addEventListener('loadeddata', warm, { once: true });
      try { video.load(); } catch (error) {}
    }
  };

  StagePlayer.prototype.handleMediaChange = function () {
    this.cancelMotion();
    this.abortBake();
    this.selectActiveVideo();
    if (this.activeVideo) {
      this.setRenderMode('video');
      this.prepareVideo(this.activeVideo, true);
      if (this.reverseVideo) this.prepareVideo(this.reverseVideo, false);
    }
  };

  StagePlayer.prototype.videoReady = function () {
    var v = this.activeVideo;
    return !!v && isFinite(v.duration) && v.duration > 0;
  };

  StagePlayer.prototype.safeDuration = function () {
    return Math.max(this.activeVideo.duration - 0.04, 0);
  };

  StagePlayer.prototype.seekToTime = function (time) {
    var v = this.activeVideo;
    if (!v) return;
    var t = Math.max(0, Math.min(time, this.safeDuration()));
    if (Math.abs(v.currentTime - t) < this.seekThreshold) return;
    try { v.currentTime = t; } catch (error) {}
  };

  // 串行 seek：始终只追最新目标，等当前 seek 的 seeked 事件后再发下一次。
  // 这样反向逐帧 seek 不会积压被浏览器丢弃，从“瞬移”变成平滑步进。
  StagePlayer.prototype.requestSeek = function (time) {
    if (!this.activeVideo) return;
    this.pendingSeekTime = Math.max(0, Math.min(time, this.safeDuration()));
    if (this.isSeeking) return;
    this.commitSeek();
  };

  StagePlayer.prototype.commitSeek = function () {
    var v = this.activeVideo;
    if (!v || this.pendingSeekTime === null) return;
    var t = this.pendingSeekTime;
    this.pendingSeekTime = null;
    if (Math.abs(v.currentTime - t) < this.seekThreshold) return;
    this.isSeeking = true;
    var self = this;
    if (this.seekWatchdog) window.clearTimeout(this.seekWatchdog);
    // 兜底：极少数情况下 seeked 不触发，超时强制推进，避免卡住
    this.seekWatchdog = window.setTimeout(function () {
      if (self.isSeeking) { self.isSeeking = false; self.commitSeek(); }
    }, 220);
    try { v.currentTime = t; } catch (error) { this.isSeeking = false; }
  };

  StagePlayer.prototype.onSeeked = function (video) {
    if (video !== this.activeVideo) return;
    this.isSeeking = false;
    if (this.seekWatchdog) { window.clearTimeout(this.seekWatchdog); this.seekWatchdog = null; }
    if (this.pendingSeekTime !== null) this.commitSeek();
  };

  StagePlayer.prototype.seekTo = function (progress) {
    if (!this.videoReady()) return;
    this.seekToTime(clamp01(progress) * this.activeVideo.duration);
  };

  // 立即定位（初始化用，无动画）
  StagePlayer.prototype.jumpTo = function (target) {
    if (this.baked) { this.jumpToBaked(target); return; }
    this.targetProgress = clamp01(target);
    this.displayProgress = this.targetProgress;
    if (this.videoReady()) this.seekTo(this.displayProgress);
    else this.pendingTarget = this.targetProgress;
  };

  // 视频就绪后补执行待处理目标
  StagePlayer.prototype.flushPending = function () {
    if (this.pendingIntroSeconds !== null) {
      var s = this.pendingIntroSeconds;
      this.pendingIntroSeconds = null;
      this.goToSeconds(s);
    } else if (this.pendingTarget !== null) {
      var t = this.pendingTarget;
      this.pendingTarget = null;
      this.goTo(t);
    } else {
      this.seekTo(this.displayProgress);
    }
  };

  // 取消进行中的过渡
  StagePlayer.prototype.cancelMotion = function () {
    if (this.motionRaf) {
      window.cancelAnimationFrame(this.motionRaf);
      this.motionRaf = null;
    }
    if (this.tween) {
      this.tween.kill();
      this.tween = null;
    }
    if (this.frameTween) {
      this.frameTween.kill();
      this.frameTween = null;
    }
    if (this.seekWatchdog) {
      window.clearTimeout(this.seekWatchdog);
      this.seekWatchdog = null;
    }
    this.isSeeking = false;
    this.pendingSeekTime = null;
    var v = this.activeVideo;
    if (v) {
      try { v.pause(); } catch (error) {}
      v.playbackRate = 1;
    }
    var r = this.reverseVideo;
    if (r) {
      try { r.pause(); } catch (error) {}
      r.playbackRate = 1;
    }
  };

  // 平滑过渡到目标进度（按钮点击用）
  StagePlayer.prototype.goTo = function (target) {
    if (this.baked) { this.goToBaked(target); return; }
    this.targetProgress = clamp01(target);
    if (!this.videoReady()) {
      this.pendingTarget = this.targetProgress;
      this.displayProgress = this.targetProgress;
      return;
    }
    this.cancelMotion();

    var v = this.activeVideo;
    var fromTime = clamp01(this.displayProgress) * v.duration;
    var toTime = Math.max(0, Math.min(this.targetProgress * v.duration, this.safeDuration()));
    var delta = toTime - fromTime;

    if (this.reducedMotion || Math.abs(delta) < 0.03) {
      this.seekToTime(toTime);
      this.displayProgress = this.targetProgress;
      return;
    }

    if (delta > 0) {
      this.playForward(fromTime, toTime);
    } else if (this.reverseVideo && this.reverseVideoReady()) {
      this.playReverse(fromTime, toTime);
    } else {
      this.tweenSeek(fromTime, toTime);
    }
  };

  // 以“视频时间（秒）”为目标平滑过渡（第一个按钮 / 进入视口自动播放用）
  StagePlayer.prototype.goToSeconds = function (sec) {
    this.pendingIntroSeconds = null;
    var v = this.activeVideo;
    var dur = v ? v.duration : 0;
    if (!this.videoReady() || !isFinite(dur) || dur <= 0) {
      this.pendingIntroSeconds = sec;
      return;
    }
    this.goTo(clamp01(sec / dur));
  };

  // 正向：原生播放到目标时间，最丝滑
  StagePlayer.prototype.playForward = function (fromTime, toTime) {
    var self = this;
    var v = this.activeVideo;
    this.ensureVisible(v, fromTime, function () {
      var distance = toTime - fromTime;
      v.playbackRate = Math.max(MIN_RATE, Math.min(MAX_RATE, distance / self.transition));
      var played = v.play();
      if (played && typeof played.catch === 'function') {
        played.catch(function () {
          // 自动播放被拒：退化为补间 seek
          self.tweenSeek(fromTime, toTime);
        });
      }
      var watch = function () {
        if (!self.activeVideo || self.activeVideo !== v) { self.motionRaf = null; return; }
        if (isFinite(v.duration) && v.duration > 0) {
          self.displayProgress = clamp01(v.currentTime / v.duration);
        }
        if (v.currentTime >= toTime - 0.012) {
          try { v.pause(); } catch (error) {}
          v.playbackRate = 1;
          self.seekToTime(toTime);
          self.displayProgress = self.targetProgress;
          self.motionRaf = null;
          return;
        }
        self.motionRaf = window.requestAnimationFrame(watch);
      };
      self.motionRaf = window.requestAnimationFrame(watch);
    });
  };

  StagePlayer.prototype.reverseVideoReady = function () {
    var r = this.reverseVideo;
    return !!r && isFinite(r.duration) && r.duration > 0;
  };

  // 反向：播放倒放视频（其正向时间轴 = 原视频反向），让回退也丝滑
  StagePlayer.prototype.playReverse = function (fromTime, toTime) {
    var self = this;
    var fwd = this.activeVideo;
    var rev = this.reverseVideo;
    var fDur = fwd.duration;
    var rDur = rev.duration || fDur;
    var fromProgress = clamp01(fromTime / fDur);
    var toProgress = clamp01(toTime / fDur);
    // 倒放视频时间映射：revTime = (1 - progress) * rDur
    var revSafe = Math.max(rDur - 0.04, 0);
    var revFrom = Math.max(0, Math.min((1 - fromProgress) * rDur, revSafe));
    var revTo = Math.max(0, Math.min((1 - toProgress) * rDur, revSafe));
    this.ensureVisible(rev, revFrom, function () {
      var distance = revTo - revFrom;
      if (distance <= 0.012) {
        self.displayProgress = self.targetProgress;
        return;
      }
      rev.playbackRate = Math.max(MIN_RATE, Math.min(MAX_RATE, distance / self.transition));
      var played = rev.play();
      if (played && typeof played.catch === 'function') {
        played.catch(function () {
          // 倒放视频自动播放被拒：切回正向视频并补间 seek
          self.setVisibleVideo(fwd);
          self.tweenSeek(fromTime, toTime);
        });
      }
      var watch = function () {
        if (self.reverseVideo !== rev) { self.motionRaf = null; return; }
        if (rDur > 0) self.displayProgress = clamp01(1 - rev.currentTime / rDur);
        if (rev.currentTime >= revTo - 0.012) {
          try { rev.pause(); } catch (error) {}
          rev.playbackRate = 1;
          self.displayProgress = self.targetProgress;
          self.motionRaf = null;
          return;
        }
        self.motionRaf = window.requestAnimationFrame(watch);
      };
      self.motionRaf = window.requestAnimationFrame(watch);
    });
  };

  // 反向（视频不能倒放）或 play 被拒时：用 GSAP 补间 currentTime + 串行 seek，平滑回退
  StagePlayer.prototype.tweenSeek = function (fromTime, toTime) {
    var self = this;
    var duration = self.transition;
    ensureGsap(this.gsapSrc).then(function (gsap) {
      if (!self.activeVideo) return;
      var proxy = { t: fromTime };
      self.tween = gsap.to(proxy, {
        t: toTime,
        duration: duration,
        ease: 'power2.inOut',
        onUpdate: function () { self.requestSeek(proxy.t); },
        onComplete: function () {
          self.tween = null;
          self.requestSeek(toTime);
          self.displayProgress = self.targetProgress;
        }
      });
    }).catch(function () {
      self.rafSeek(fromTime, toTime, duration);
    });
  };

  // GSAP 不可用时的反向补间退化（rAF + ease-in-out）
  StagePlayer.prototype.rafSeek = function (fromTime, toTime, duration) {
    var self = this;
    var now = function () {
      return (window.performance && performance.now) ? performance.now() : Date.now();
    };
    var start = now();
    var ms = duration * 1000;
    var ease = function (t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; };
    var tick = function () {
      var p = Math.min(1, (now() - start) / ms);
      self.requestSeek(fromTime + (toTime - fromTime) * ease(p));
      if (p >= 1) {
        self.motionRaf = null;
        self.displayProgress = self.targetProgress;
        return;
      }
      self.motionRaf = window.requestAnimationFrame(tick);
    };
    this.motionRaf = window.requestAnimationFrame(tick);
  };

  // —— 预烘焙帧：把视频解码成 ImageBitmap 缓存，正/反向都画缓存帧 ——
  // 配置了倒放视频时，反向直接播倒放视频，无需烘焙
  StagePlayer.prototype.onVideoReady = function () {
    this.flushPending();
    if (!this.hasReverse) this.startBake();
  };

  StagePlayer.prototype.currentFrameIndex = function () {
    return Math.round(this.displayProgress * (this.frameCount - 1));
  };

  StagePlayer.prototype.resizeCanvas = function () {
    if (!this.canvas || !this.ctx) return;
    var rect = this.stage.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
    if (this.baked) this.drawFrame(this.currentFrameIndex());
  };

  StagePlayer.prototype.drawFrame = function (index) {
    if (!this.ctx || !this.frames.length) return;
    var i = Math.max(0, Math.min(this.frameCount - 1, index));
    var bmp = this.frames[i];
    if (!bmp) return;
    var cw = this.canvas.width;
    var ch = this.canvas.height;
    var scale = Math.min(cw / bmp.width, ch / bmp.height);
    var dw = bmp.width * scale;
    var dh = bmp.height * scale;
    var dx = (cw - dw) / 2;
    var dy = (ch - dh) / 2;
    this.ctx.clearRect(0, 0, cw, ch);
    this.ctx.drawImage(bmp, dx, dy, dw, dh);
  };

  // 优先 fetch 成 blob（同源 objectURL 读像素不污染 canvas），失败再退回 crossOrigin 直载
  StagePlayer.prototype.startBake = function () {
    if (!this.canBake || this.baked || this.baking) return;
    if (this.renderMode !== 'video') return;
    var v = this.activeVideo;
    if (!v) return;
    var srcEl = v.querySelector('source');
    var src = v.currentSrc || (srcEl && srcEl.src);
    if (!src) return;
    var self = this;
    this.baking = true;
    this.stage.dataset.bake = 'baking';
    if (typeof window.fetch === 'function') {
      window.fetch(src, { mode: 'cors', credentials: 'omit' })
        .then(function (resp) {
          if (!resp.ok) throw new Error('bake fetch ' + resp.status);
          return resp.blob();
        })
        .then(function (blob) {
          if (!self.baking) return;
          self.bakeObjectUrl = URL.createObjectURL(blob);
          self.beginBake(self.bakeObjectUrl, false);
        })
        .catch(function () {
          if (!self.baking) return;
          self.beginBake(src, true); // 退回带 crossOrigin 直载
        });
    } else {
      this.beginBake(src, true);
    }
  };

  // 建离屏 video 逐帧 seek 抓取，避免干扰可见视频交互
  StagePlayer.prototype.beginBake = function (url, useCrossOrigin) {
    var self = this;
    this.frameCount = this.videoQuery.matches ? BAKE_SAMPLES_MOBILE : BAKE_SAMPLES_DESKTOP;
    var maxW = this.videoQuery.matches ? BAKE_MAXW_MOBILE : BAKE_MAXW_DESKTOP;
    var bv = document.createElement('video');
    if (useCrossOrigin) bv.crossOrigin = 'anonymous';
    bv.muted = true;
    bv.defaultMuted = true;
    bv.playsInline = true;
    bv.preload = 'auto';
    bv.setAttribute('muted', '');
    bv.setAttribute('playsinline', '');
    bv.setAttribute('webkit-playsinline', '');
    this.bakeVideo = bv;
    var onMeta = function () {
      var vw = bv.videoWidth || maxW;
      var vh = bv.videoHeight || Math.round(maxW * 9 / 16);
      var ratio = vw && vh ? vw / vh : 16 / 9;
      var bw = Math.min(vw, maxW);
      var bh = Math.max(1, Math.round(bw / ratio));
      self.bakeCanvas = document.createElement('canvas');
      self.bakeCanvas.width = bw;
      self.bakeCanvas.height = bh;
      self.bakeCtx = self.bakeCanvas.getContext('2d');
      var begin = function () {
        try { bv.pause(); } catch (e) {}
        self.bakeIndex = 0;
        self.bakeNext();
      };
      var played = bv.play();
      if (played && typeof played.then === 'function') played.then(begin).catch(begin);
      else begin();
    };
    bv.addEventListener('loadedmetadata', onMeta, { once: true });
    bv.addEventListener('error', function () { self.abortBake(); }, { once: true });
    try { bv.src = url; bv.load(); } catch (e) { this.abortBake(); }
  };

  StagePlayer.prototype.bakeNext = function () {
    var self = this;
    var bv = this.bakeVideo;
    if (!bv || !this.baking) return;
    if (this.bakeIndex >= this.frameCount) { this.finishBake(); return; }
    var dur = Math.max((bv.duration || 0) - 0.04, 0);
    if (!isFinite(dur) || dur <= 0) { this.abortBake(); return; }
    var t = this.frameCount <= 1 ? 0 : (this.bakeIndex / (this.frameCount - 1)) * dur;
    var watchdog;
    var grab = function () {
      bv.removeEventListener('seeked', onSeeked);
      window.clearTimeout(watchdog);
      try {
        self.bakeCtx.drawImage(bv, 0, 0, self.bakeCanvas.width, self.bakeCanvas.height);
      } catch (e) { self.abortBake(); return; }
      window.createImageBitmap(self.bakeCanvas).then(function (bmp) {
        if (!self.baking) { if (bmp && bmp.close) bmp.close(); return; }
        self.frames[self.bakeIndex] = bmp;
        self.bakeIndex++;
        self.bakeNext();
      }).catch(function () { self.abortBake(); });
    };
    var onSeeked = function () { grab(); };
    bv.addEventListener('seeked', onSeeked);
    watchdog = window.setTimeout(function () {
      bv.removeEventListener('seeked', onSeeked);
      if (bv.readyState >= 2) grab();
      else self.abortBake();
    }, 800);
    try { bv.currentTime = t; } catch (e) { window.clearTimeout(watchdog); this.abortBake(); }
  };

  StagePlayer.prototype.finishBake = function () {
    this.baking = false;
    this.releaseBakeVideo();
    if (this.frames.length < 2) { this.abortBake(); return; }
    this.frameCount = this.frames.length;
    this.baked = true;
    this.stage.dataset.bake = 'ready';
    this.cancelMotion();
    this.setRenderMode('frames');
    this.resizeCanvas();
    this.displayProgress = this.targetProgress;
    this.drawFrame(Math.round(this.targetProgress * (this.frameCount - 1)));
  };

  StagePlayer.prototype.abortBake = function () {
    this.baking = false;
    this.releaseBakeVideo();
    this.closeFrames();
    this.baked = false;
    this.stage.dataset.bake = 'failed';
  };

  StagePlayer.prototype.releaseBakeVideo = function () {
    var bv = this.bakeVideo;
    if (bv) {
      try { bv.pause(); } catch (e) {}
      try { bv.removeAttribute('src'); bv.load(); } catch (e) {}
    }
    if (this.bakeObjectUrl) {
      try { URL.revokeObjectURL(this.bakeObjectUrl); } catch (e) {}
      this.bakeObjectUrl = null;
    }
    this.bakeVideo = null;
    this.bakeCanvas = null;
    this.bakeCtx = null;
  };

  StagePlayer.prototype.closeFrames = function () {
    if (this.frames && this.frames.length) {
      this.frames.forEach(function (b) { if (b && b.close) b.close(); });
    }
    this.frames = [];
  };

  StagePlayer.prototype.jumpToBaked = function (target) {
    this.targetProgress = clamp01(target);
    this.displayProgress = this.targetProgress;
    this.drawFrame(Math.round(this.targetProgress * (this.frameCount - 1)));
  };

  // frames 模式：GSAP 补间进度并画缓存帧，正/反向完全对称、零解码延迟
  StagePlayer.prototype.goToBaked = function (target) {
    var self = this;
    this.targetProgress = clamp01(target);
    this.cancelMotion();
    var from = this.displayProgress;
    var to = this.targetProgress;
    if (this.reducedMotion || Math.abs(to - from) < 0.001) {
      this.displayProgress = to;
      this.drawFrame(Math.round(to * (this.frameCount - 1)));
      return;
    }
    var duration = self.transition;
    ensureGsap(this.gsapSrc).then(function (gsap) {
      var proxy = { p: from };
      self.frameTween = gsap.to(proxy, {
        p: to,
        duration: duration,
        ease: 'power2.inOut',
        onUpdate: function () {
          self.displayProgress = proxy.p;
          self.drawFrame(Math.round(proxy.p * (self.frameCount - 1)));
        },
        onComplete: function () {
          self.frameTween = null;
          self.displayProgress = to;
          self.drawFrame(Math.round(to * (self.frameCount - 1)));
        }
      });
    }).catch(function () {
      self.rafFrames(from, to, duration);
    });
  };

  // GSAP 不可用时的帧补间退化
  StagePlayer.prototype.rafFrames = function (from, to, duration) {
    var self = this;
    var now = function () {
      return (window.performance && performance.now) ? performance.now() : Date.now();
    };
    var start = now();
    var ms = duration * 1000;
    var ease = function (t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; };
    var tick = function () {
      var p = Math.min(1, (now() - start) / ms);
      var val = from + (to - from) * ease(p);
      self.displayProgress = val;
      self.drawFrame(Math.round(val * (self.frameCount - 1)));
      if (p >= 1) {
        self.motionRaf = null;
        self.displayProgress = to;
        return;
      }
      self.motionRaf = window.requestAnimationFrame(tick);
    };
    this.motionRaf = window.requestAnimationFrame(tick);
  };

  StagePlayer.prototype.destroy = function () {
    this.cancelMotion();
    this.abortBake();
    this.closeFrames();
    if (this._masIntroObserver) {
      try { this._masIntroObserver.disconnect(); } catch (e) {}
      this._masIntroObserver = null;
    }
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.videoQuery.removeEventListener) {
      this.videoQuery.removeEventListener('change', this.mediaChangeHandler);
    } else if (this.videoQuery.removeListener) {
      this.videoQuery.removeListener(this.mediaChangeHandler);
    }
  };

  function initSection(root) {
    if (!root || root.dataset.masInited === 'true') return;
    root.dataset.masInited = 'true';

    var stage = root.querySelector('[data-mas-stage]');
    var buttons = Array.prototype.slice.call(
      root.querySelectorAll('[data-mas-btn]')
    );
    if (!stage || !buttons.length) return;

    var player = new StagePlayer(stage);
    root._masPlayer = player;

    function centerButton(btn) {
      var track = btn && btn.parentElement;
      if (!track || track.scrollWidth <= track.clientWidth) return;

      var targetLeft = btn.offsetLeft - ((track.clientWidth - btn.offsetWidth) / 2);
      var maxLeft = track.scrollWidth - track.clientWidth;
      targetLeft = Math.max(0, Math.min(maxLeft, targetLeft));

      if (track.scrollTo) {
        track.scrollTo({
          left: targetLeft,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      } else {
        track.scrollLeft = targetLeft;
      }
    }

    function setActive(btn) {
      buttons.forEach(function (b) {
        var isActive = b === btn;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      centerButton(btn);
    }

    function progressOf(btn) {
      var value = parseFloat(btn.getAttribute('data-target-progress'));
      return clamp01((isNaN(value) ? 0 : value) / 100);
    }

    buttons.forEach(function (btn, index) {
      btn.addEventListener('click', function () {
        setActive(btn);
        if (index === 0) {
          player.goToSeconds(player.introSeconds);
        } else {
          player.goTo(progressOf(btn));
        }
      });
    });

    // 初始停在视频第一帧，等滚动进入视口后自动播放到 introSeconds
    player.jumpTo(0);

    // 滚动进入视口：从第一帧自动播放到 introSeconds 并激活第一个按钮（仅首次）
    var startIntro = function () {
      setActive(buttons[0]);
      player.goToSeconds(player.introSeconds);
    };
    if ('IntersectionObserver' in window) {
      var introDone = false;
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting && !introDone) {
            introDone = true;
            io.disconnect();
            startIntro();
            break;
          }
        }
      }, { threshold: 0.4 });
      io.observe(root);
      player._masIntroObserver = io;
    } else {
      startIntro();
    }
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('.model-angle-switcher');
    Array.prototype.forEach.call(nodes, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify Theme Editor 兼容
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target && e.target.querySelector
      ? e.target.querySelector('.model-angle-switcher')
      : null;
    if (root && root._masPlayer) root._masPlayer.destroy();
  });
})();
